import React from 'react';
import StationAvatar from './StationAvatar';

const NowPlayingBar = ({
  currentPlayingStation,
  isPlaying,
  isBuffering,
  currentVolume,
  playDuration,
  formatPlayDuration,
  getAudioQuality,
  onPlayPause,
  onVolumeChange,
  onMuteToggle,
  onIdentify,
  identifyState,
  onPrevious,
  onNext,
  isCurrentFavorite,
  onToggleFavorite,
  audioRef,
}) => {
  const isActuallyPlaying = isPlaying && currentPlayingStation && audioRef.current && !audioRef.current.paused;

  const country = currentPlayingStation
    ? currentPlayingStation.country_name || currentPlayingStation.country
    : null;
  const rawTag = currentPlayingStation?.tags || '';
  const primaryTag = rawTag.split(',')[0].trim();
  const tagLabel = primaryTag ? primaryTag.split(' ')[0].slice(0, 14) : null;
  const quality = currentPlayingStation ? getAudioQuality(currentPlayingStation) : null;

  const isIdentifying = identifyState === 'loading';

  return (
    <div className="now-playing-bar">
      {/* Station info */}
      <div className="player-station-info">
        <div className={`player-artwork${isActuallyPlaying ? ' is-playing' : ''}${!currentPlayingStation ? ' is-idle' : ''}`}>
          {isActuallyPlaying ? (
            /* Show logo + animated equalizer overlay when playing */
            <div className="player-artwork-playing">
              <StationAvatar
                name={currentPlayingStation.name}
                favicon={currentPlayingStation.favicon}
                homepage={currentPlayingStation.homepage}
                size={44}
              />
              <div className="player-artwork-eq-overlay">
                <div className="equalizer">
                  <div className="equalizer-bar" />
                  <div className="equalizer-bar" />
                  <div className="equalizer-bar" />
                  <div className="equalizer-bar" />
                  <div className="equalizer-bar" />
                </div>
              </div>
            </div>
          ) : currentPlayingStation ? (
            <StationAvatar
              name={currentPlayingStation.name}
              favicon={currentPlayingStation.favicon}
              homepage={currentPlayingStation.homepage}
              size={44}
            />
          ) : (
            <div className="player-artwork-idle" aria-hidden="true">
              <span className="player-artwork-idle-icon">▶</span>
            </div>
          )}
        </div>

        <div className="player-text">
          {currentPlayingStation ? (
            <>
              <div className="player-station-header-row">
                <div className="player-station-name">
                  {currentPlayingStation.name}
                </div>
              </div>
              <div className="player-station-meta">
                {isBuffering ? (
                  <span className="player-pill player-pill-buffering">
                    <span className="player-buffering-dots"><span/><span/><span/></span>
                    Buffering
                  </span>
                ) : (
                  isActuallyPlaying && <span className="player-pill player-pill-live">LIVE</span>
                )}
                {country && <span className="player-pill">{country}</span>}
                {tagLabel && <span className="player-pill">{tagLabel}</span>}
                {quality && <span className="player-pill player-pill-muted">{quality}</span>}
              </div>
            </>
          ) : (
            <div className="player-idle-state">
              <div className="player-idle-text">Select a station to start listening</div>
              <div className="player-idle-hint">Browse countries or search below</div>
            </div>
          )}
        </div>
      </div>

      <div className="player-bar-actions">
        {/* Playback controls */}
        <div className="player-controls">
          {onToggleFavorite && (
            <button
              className={`player-btn player-favorite-btn${isCurrentFavorite ? ' is-active' : ''}`}
              onClick={onToggleFavorite}
              disabled={!currentPlayingStation}
              title={isCurrentFavorite ? 'Remove from favourites' : 'Add to favourites'}
              aria-label={isCurrentFavorite ? 'Remove from favourites' : 'Add to favourites'}
            >
              {isCurrentFavorite ? '♥' : '♡'}
            </button>
          )}
          <button
            className="player-btn"
            onClick={onPrevious}
            disabled={!currentPlayingStation}
            title="Previous station"
            aria-label="Previous station"
          >
            ⏮
          </button>

          <button
            className="player-btn player-btn-play"
            onClick={onPlayPause}
            disabled={!currentPlayingStation}
            title={isActuallyPlaying ? 'Pause' : 'Play'}
            aria-label={isActuallyPlaying ? 'Pause' : 'Play'}
          >
            {isActuallyPlaying ? '⏸' : '▶'}
          </button>

          <button
            className="player-btn"
            onClick={onNext}
            disabled={!currentPlayingStation}
            title="Next station"
            aria-label="Next station"
          >
            ⏭
          </button>
        </div>

        {/* Identify song button */}
        {currentPlayingStation && (
          <button
            className={`player-btn identify-btn${isIdentifying ? ' is-loading' : ''}`}
            onClick={onIdentify}
            disabled={isIdentifying}
            title="Identify current song"
            aria-label="Identify current song"
          >
            {isIdentifying ? (
              <svg className="identify-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
            )}
          </button>
        )}

        {/* Volume */}
        <div className="player-volume">
        <button
          className="volume-icon-btn"
          onClick={onMuteToggle}
          title={currentVolume === 0 ? 'Unmute' : 'Mute'}
          aria-label={currentVolume === 0 ? 'Unmute' : 'Mute'}
        >
          {currentVolume === 0 ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
          ) : currentVolume < 0.5 ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
          )}
        </button>
        <input
          type="range"
          min="0"
          max="100"
          value={Math.round(currentVolume * 100)}
          onChange={(e) => onVolumeChange(parseInt(e.target.value, 10) / 100)}
          className="volume-slider"
          aria-label="Volume"
          style={{
            background: `linear-gradient(to right, var(--accent) 0%, var(--accent) ${Math.round(currentVolume * 100)}%, var(--border) ${Math.round(currentVolume * 100)}%, var(--border) 100%)`
          }}
        />
        </div>
      </div>
    </div>
  );
};

export default NowPlayingBar;
